<template>
  <div class="flight-schedule">
    <div class="flight-schedule__loading-indicator" v-if="!loaded">
      <Loader />
    </div>
    <div class="flight-schedule__filters" v-if="loaded">
      <div>
        <select v-model="filter.departure">
          <option v-for="departure in departures" :key="departure.code" :value="departure.code">{{ formatListItem(departure, 'Wylot z...') }}</option>
        </select>
      </div>
      <div>
        <select v-model="filter.destination">
          <option v-for="destination in destinations" :key="destination.code" :value="destination.code">{{ formatListItem(destination, 'Port lotniczy') }}</option>
        </select>
      </div>
      <div class="radio">
        <div>
          <input id="filtersTo" type="radio" value="to" v-model="filter.direction" @change="swapDepartureAndDestination">
          <label for="filtersTo">Wylot</label>
        </div>
        <div>
          <input id="filtersFrom" type="radio" value="from" v-model="filter.direction" @change="swapDepartureAndDestination">
          <label for="filtersFrom">Powrót</label>
        </div>
      </div>
      <div>
        <input class="flightNumber" type="text" v-model="filter.flight" placeholder="Nr lotu">
      </div>
    </div>
    <table class="flight-schedule__table" v-if="loaded">
      <thead @click.prevent="sort($event.target.dataset.field)">
        <tr>
          <th rowspan="2" data-field="fromAirport" :class="getSortClassesForColumn('fromAirport')">
            Wylot z
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th rowspan="2" :class="getSortClassesForColumn('')">&nbsp;</th>
          <th rowspan="2" data-field="toAirport" :class="getSortClassesForColumn('toAirport')">
            Port lotniczy
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th rowspan="2" data-field="flightNumber" :class="getSortClassesForColumn('flightNumber')">
            Nr lotu
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th rowspan="2" data-field="weekDay" :class="getSortClassesForColumn('weekDay')">
            Dni tygodnia
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th colspan="2" :class="getSortClassesForColumn('')">Terminy lotów</th>
          <th rowspan="2" data-field="takeoffTime" :class="getSortClassesForColumn('takeoffTime')">
            Godzina odlotu
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th rowspan="2" data-field="landingTime" :class="getSortClassesForColumn('landingTime')">
            Godzina przylotu
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th rowspan="2" data-field="flightTime" :class="getSortClassesForColumn('flightTime')">
            Czas lotu
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th rowspan="2" data-field="carrierName" :class="getSortClassesForColumn('carrierName')">
            Przewoźnik
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
        </tr>
        <tr>
          <th data-field="fromDate" :class="getSortClassesForColumn('fromDate')">
            Od
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
          <th data-field="toDate" :class="getSortClassesForColumn('toDate')">
            Do
            <Icon icon="arrow-up-filled"/>
            <Icon icon="arrow-down-filled"/>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(schedule, index) in schedules" :key="schedule.id"
            :class="{ even: index % 2 == 0, odd: index % 2 != 0 }"
            :data-direction="schedule.flightDirection"
            :data-from-airport-code="schedule.fromAirportCode"
            :data-to-airport-code="schedule.toAirportCode"
            :data-flight-number="schedule.flightNumber"
            :data-week-day="schedule.weekDay"
            :data-from-date="schedule.fromDate"
            :data-to-date="schedule.toDate"
            :data-takeoff-time="schedule.takeoffTime"
            :data-landing-time="schedule.landingTime"
            :data-flight-time="schedule.flightTime"
            :data-carrier-name="schedule.carrierName"
          >
          <td class="departure" width="156">{{ schedule.fromAirport }} ({{ schedule.fromAirportCode }})</td>
          <td width="10"><Icon icon="fat-arrow-right" color="#0c5ea7" /></td>
          <td class="arrival" width="155">{{ schedule.toAirport }} ({{ schedule.toAirportCode }})</td>
          <td width="118">{{ schedule.flightNumber }}</td>
          <td width="124">{{ weekDay[schedule.weekDay] }}</td>
          <td width="108">{{ schedule.fromDate }}</td>
          <td width="108">{{ schedule.toDate }}</td>
          <td width="108">{{ schedule.takeoffTime }}</td>
          <td width="114">{{ schedule.landingTime }}</td>
          <td width="109">{{ schedule.flightTime }}</td>
          <td width="139">{{ schedule.carrierName }}</td>
        </tr>
        <tr class="flight-schedule__table--empty" v-if="schedules.length === 0">
          <td colspan="11">Brak lotów</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'

import Icon from '@/components/Icon.vue'
import Loader from '@/components/Loader.vue'
import { API } from '@/modules/api'

const EMPTY_LIST_ITEM = { code: '', name: 'test' }

@Component({
  components: {
    Icon,
    Loader,
  },
})
export default class FlightSchedule extends Vue {
  loaded = false
  sourceData = []
  weekDay = []

  filter = {
    direction: 'to',
    departure: EMPTY_LIST_ITEM.code,
    destination: EMPTY_LIST_ITEM.code,
    flight: null,
  }

  order = 'fromDate'
  direction = true

  mounted () {
    new API().getFlightScheduleData()
      .then(response => { this.sourceData = response.flights; this.weekDay = response.weekDay })
      .then(() => { this.loaded = true })
  }

  get schedules () {
    const result = this.sourceData.filter(schedule =>
      schedule.flightDirection === this.filter.direction
      && (!this.filter.departure || this.filter.departure === schedule.fromAirportCode)
      && (!this.filter.destination || this.filter.destination === schedule.toAirportCode)
      && (!this.filter.flight || schedule.flightNumber.contains(this.filter.flight))
    )

    if (this.order) {
      return result.sort(this.compareSchedules)
    } else {
      return result
    }
  }

  get departures () {
    const departures = this.sourceData
      .filter(schedule => schedule.flightDirection === this.filter.direction)
      .map(schedule => ({ code: schedule.fromAirportCode, name: schedule.fromAirport }))
      .uniq(departure => departure.code)

    return [ EMPTY_LIST_ITEM, ...departures ]
  }

  get destinations () {
    const destinations = this.sourceData
      .filter(schedule => schedule.flightDirection === this.filter.direction)
      .map(schedule => ({ code: schedule.toAirportCode, name: schedule.toAirport }))
      .uniq(destination => destination.code)

    return [ EMPTY_LIST_ITEM, ...destinations ]
  }

  swapDepartureAndDestination () {
    const departure = this.filter.departure
    this.filter.departure = this.filter.destination
    this.filter.destination = departure
  }

  formatListItem (item, defaultText = '') {
    if (item.code === EMPTY_LIST_ITEM.code) return defaultText
    else return `${item.name} (${item.code})`
  }

  sort (field) {
    if (this.order === field) {
      this.direction = !this.direction
    } else if (field) {
      this.order = field
      this.direction = true
    }
  }

  getSortClassesForColumn (column) {
    if (this.order === column && this.schedules.length) {
      return {
        'flight-schedule__header': true,
        'flight-schedule__header--active': true,
        'flight-schedule__header--asc': this.direction,
        'flight-schedule__header--desc': !this.direction,
      }
    } else {
      return { 
        'flight-schedule__header': true,
      }
    }
  }

  compareSchedules (f1, f2) {
    if (this.direction) {
      return f1[this.order].compareTo(f2[this.order])
    } else {
      return f2[this.order].compareTo(f1[this.order])
    }
  }
}
</script>

<style lang="scss">
.flight-schedule {
  background-color: #005ca9;
  color: #fff;
  position: relative;
  width: 100%;
  max-width: 1240px;
  margin: 5px auto 10px;
  min-height: 100px;  

  &__filters {
    display: -ms-grid;
    display: grid;
    -ms-grid-columns: 1fr 30px 1fr 30px 100px 30px 1fr;
    grid-template-columns: 1fr 1fr 100px 1fr;
    grid-column-gap: 30px;
    padding: 25px;

    .radio {
      margin: 0;
      padding: 0;
    }

    .flightNumber {
      color: #535353;
      border: 0;
      border-radius: 0;
      width: 100%;
      line-height: 31px;
      padding: 0 5px;
    }

    .flightNumber, select {
      height: 100%;
    }

    select {
      color: #fff;
      font-weight: normal;
    }
    option {
      color: #535353;
    }
  }

  &__table {
    width: 100%;
    color: #535353;
    font-size: 1.3rem;
    font-family: 'Gotham Book';
    font-weight: 400;
    font-style: normal;
    text-align: center;

    tr.even {
      background-color: white;
    }

    tr.odd {
      background-color: rgb(240, 240, 240);
    }

    td {
      cursor: default;
      padding: 12px 10px;
    }

    .departure, .arrival {
      font-weight: bold;
    }

    thead {
      border-top: 1px solid #adadad;
      border-bottom: 1px solid #adadad;
    }

    &--empty {
      text-align: center;
      color: #ffffff;
      font-weight: bold;
    }
  }

  &__header {
    background-color: #eeeeee;
    color: #787878;
    font-size: 12px;
    font-size: 1.2rem;
    font-family: 'Gotham';
    font-weight: 500;
    font-style: normal;
    text-transform: uppercase;
    padding: 10px 13px;
    text-align: center;
    position: relative;

    &[data-field] {
      cursor: pointer;
      user-select: none;
    }

    &--active {
      background-color: #00ACE9;
      color: #ffffff;
    }

    .figure {
      position: absolute;
      right: 0;
      top: 50%;
      padding-right: 2px;
      pointer-events: none;

      &--arrow-up-filled {
        transform: translate(0, -10px);
      }
      &--arrow-down-filled {
        transform: translate(0, -3px);
      }
    }

    &--asc .figure--arrow-up-filled,
    &--desc .figure--arrow-down-filled {
      display: none;
    }
  }

  .watersplash {
    align-items: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/flight-schedule/FlightSchedule.vue